import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, ShieldCheck, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GroupCard } from "@/components/Cards";
import api from "@/lib/api";
import useLocalizedHero from "@/lib/useLocalizedHero";

const COUNT_WORDS = [
    "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
    "Eight", "Nine", "Ten", "Eleven", "Twelve",
];
// Falls back to the numeral itself once we run out of spelled-out words —
// keeps the headline honest without ever throwing on an out-of-range count.
const countWord = (n) => COUNT_WORDS[n] ?? String(n);

export default function Landing() {
    const { t } = useTranslation();
    const hero = useLocalizedHero();
    const [groups, setGroups] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        api.get("/public/product-groups").then((r) => setGroups(r.data));
        api.get("/public/testimonials").then((r) => setTestimonials(r.data));
        api.get("/public/plans").then((r) => setPlans(r.data));
    }, []);

    return (
        <>
            {/* HERO */}
            <section className="relative bg-white hero-grain-left overflow-hidden">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_1.25fr] gap-8 lg:gap-12 items-center">
                    <div className="animate-fade-up" data-testid={`hero-country-${hero.country}`}>
                        <div className="inline-flex items-center gap-2 rounded-full border border-gold-200 bg-cream px-4 py-1.5 mb-6">
                            <span className="text-base leading-none">{hero.countryFlag}</span>
                            <span className="text-xs uppercase tracking-[0.25em] text-navy-700">
                                {hero.eyebrow}
                            </span>
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-navy-700 leading-[1.05] mb-6">
                            {hero.title1} <em className="text-gold-600 not-italic">{hero.titleEm}</em> {hero.title2}
                        </h1>
                        <p className="text-lg text-navy-500 max-w-lg leading-relaxed mb-8">
                            {hero.subtitle}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/signup" data-testid="hero-signup">
                                <Button className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-12 px-7 text-base font-semibold">
                                    {hero.ctaSignup} <ArrowRight className="ml-2" size={17} />
                                </Button>
                            </Link>
                            <Link to="/browse" data-testid="hero-browse">
                                <Button
                                    variant="outline"
                                    className="rounded-full h-12 px-7 text-base border-navy-700 text-navy-700 hover:bg-navy-700 hover:text-white"
                                >
                                    {hero.ctaBrowse}
                                </Button>
                            </Link>
                        </div>
                        {hero.popularCategories.length > 0 && (
                            <div
                                data-testid="hero-popular-categories"
                                className="mt-6 flex items-center gap-2 flex-wrap"
                            >
                                <span className="text-[10px] uppercase tracking-[0.2em] text-navy-400">
                                    {t("hero.popular_now", { country: hero.countryName })}
                                </span>
                                {hero.popularCategories.map((cat, i) => (
                                    <span
                                        key={`${cat}-${i}`}
                                        className="text-[11px] px-2.5 py-1 rounded-full border border-navy-100 bg-white text-navy-600"
                                    >
                                        {cat}
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="mt-8 flex items-center gap-5">
                            <img
                                src="/pink_5_stars_large.png"
                                alt="5 stars"
                                className="h-5 object-contain"
                            />
                            <div className="text-sm text-navy-500">
                                <strong className="text-navy-700">4.9/5</strong> {hero.ratingText}
                            </div>
                        </div>
                        <div className="mt-2 text-[11px] text-navy-400 tracking-wide">
                            {hero.currencyNote}
                        </div>
                    </div>

                    <div className="relative animate-fade-up stagger-2 lg:-mr-6 xl:-mr-12">
                        <video
                            key={hero.videoUrl}
                            src={hero.videoUrl}
                            poster={hero.posterUrl}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            aria-label={`Perx panda mascot animation — ${hero.countryName}`}
                            data-testid="hero-panda"
                            className="relative z-10 mx-auto w-full h-auto aspect-square object-contain bg-white select-none pointer-events-none"
                        />
                    </div>
                </div>
            </section>

            {/* VALUE PROPS */}
            <section className="bg-cream py-20">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            { icon: Sparkles, title: "Curated, not spammy", body: "Every deal earns its place. If it isn't clearly better than public, it's out." },
                            { icon: ShieldCheck, title: "Vendor-backed", body: "Real humans negotiate on your behalf and stand behind every perk." },
                            { icon: Leaf, title: "Panda Promise", body: "Beat any public price for 30 days or we refund the difference." },
                        ].map((v, i) => (
                            <div
                                key={v.title}
                                className={`rounded-2xl bg-white p-6 shadow-soft animate-fade-up stagger-${i + 1}`}
                            >
                                <v.icon size={24} className="text-gold-600 mb-4" strokeWidth={1.7} />
                                <div className="font-display text-lg text-navy-700 mb-2">
                                    {v.title}
                                </div>
                                <p className="text-sm text-navy-500 leading-relaxed">{v.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-20 bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                                The catalogue
                            </div>
                            <h2 className="font-display text-4xl md:text-5xl text-navy-700 max-w-xl">
                                {countWord(groups.length)} pocket{groups.length === 1 ? "" : "s"} of properly good deals.
                            </h2>
                        </div>
                        <Link
                            to="/browse"
                            data-testid="landing-browse-all"
                            className="text-sm font-semibold text-navy-700 hover:text-gold-600 inline-flex items-center gap-2"
                        >
                            Browse the full catalogue <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {groups.slice(0, 6).map((g) => (
                            <GroupCard key={g.id} group={g} to={`/browse/${g.id}`} />
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-20 bg-navy-700 text-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="text-center mb-14">
                        <img
                            src="/pink_5_stars_large.png"
                            alt="5 stars"
                            className="h-6 mx-auto mb-5"
                        />
                        <h2 className="font-display text-4xl md:text-5xl">
                            The flock speaks.
                        </h2>
                        <p className="text-navy-50/70 mt-3 max-w-xl mx-auto">
                            A few notes from members who use Perx daily.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {testimonials.map((t, i) => (
                            <blockquote
                                key={t.name}
                                data-testid={`testimonial-${i}`}
                                className={`rounded-2xl bg-white/5 p-6 backdrop-blur-sm animate-fade-up stagger-${(i % 4) + 1}`}
                            >
                                <img
                                    src="/pink_5_stars_large.png"
                                    alt=""
                                    className="h-3 mb-4 object-contain"
                                />
                                <p className="text-navy-50 text-sm leading-relaxed mb-6">
                                    "{t.quote}"
                                </p>
                                <footer>
                                    <div className="font-semibold text-white">{t.name}</div>
                                    <div className="text-xs text-navy-50/60 mt-0.5">
                                        {t.role}
                                    </div>
                                </footer>
                            </blockquote>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING TEASER */}
            <section className="py-20 bg-cream">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    <div className="text-center mb-14">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                            One membership
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl text-navy-700">
                            Subscribe & boost your benefits.
                        </h2>
                    </div>
                    {plans.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`plan-card-${p.id}`}
                            className="max-w-4xl mx-auto rounded-3xl bg-navy-700 text-white shadow-lift p-10 lg:p-14 grid md:grid-cols-[1fr_1px_1fr] gap-10 items-center"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-3">
                                    The Perx membership
                                </div>
                                <div className="font-display text-6xl mb-2">
                                    ${p.price_monthly}
                                    <span className="text-base font-sans text-navy-50/60"> / mo</span>
                                </div>
                                <div className="text-navy-50/70 text-lg mb-6">
                                    {p.tagline}
                                </div>
                                <Link to="/signup">
                                    <Button className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-12 px-7 font-semibold">
                                        {p.cta}
                                    </Button>
                                </Link>
                            </div>
                            <div className="hidden md:block h-full w-px bg-white/10" />
                            <ul className="space-y-3 text-sm text-navy-50/90">
                                {p.features.map((f) => (
                                    <li key={f} className="flex gap-3">
                                        <span className="text-gold-500 mt-0.5">◆</span>
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>
        </>
    );
}
